

import React, { useState, useEffect, createContext, useCallback } from 'react';
import { User, Badge, Lesson } from './types';
import Header from './components/ui/Header';
import LoadingSpinner from './components/ui/LoadingSpinner';
import StudentDashboard from './components/dashboards/StudentDashboard';
import TeacherDashboard from './components/dashboards/TeacherDashboard';
import AdminDashboard from './components/dashboards/AdminDashboard';
import SuperAdminDashboard from './components/dashboards/SuperAdminDashboard';
import ParentDashboard from './components/dashboards/ParentDashboard';
import AccessibilityModal from './components/modals/AccessibilityModal';
import BadgeAwardModal from './components/modals/BadgeAwardModal';
import LandingPage from './components/landing/LandingPage';
import GuestDashboard from './components/guest/GuestDashboard';
import JoinWithCode from './components/guest/JoinWithCode';
import TypingTutor from './components/core/TypingTutor';
import AuthPage from './components/auth/AuthPage';
import useInactivityTimer from './hooks/useInactivityTimer';
import InactivityWarningModal from './components/modals/InactivityWarningModal';
import MfaSetupModal from './components/mfa/MfaSetupModal';
import MfaVerifyModal from './components/mfa/MfaVerifyModal';
import PasswordResetPage from './components/auth/PasswordResetPage';
import ForcePasswordResetPage from './components/auth/ForcePasswordResetPage';
import { api } from './utils/api';
import Footer from './components/ui/Footer';
import TermsOfServicePage from './components/legal/TermsOfServicePage';
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage';
import AboutUsPage from './components/about/AboutUsPage';
import { useCookieConsent, CookieConsent } from './hooks/useCookieConsent';
import CookieConsentBanner from './components/cookies/CookieConsentBanner';
import CookieSettingsModal from './components/cookies/CookieSettingsModal';
import HelpCenterPage from './components/help/HelpCenterPage';


interface AccessibilityContextType {
  isHighContrast: boolean;
  setIsHighContrast: React.Dispatch<React.SetStateAction<boolean>>;
  isDyslexicFont: boolean;
  setIsDyslexicFont: React.Dispatch<React.SetStateAction<boolean>>;
  isVoiceOver: boolean;
  setIsVoiceOver: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

interface AppContextType {
  handleLogin: (newToken: string, userData: User) => void;
  promptMfaVerify: (mfaToken: string) => void;
}
export const AppContext = createContext<AppContextType | undefined>(undefined);

type View = 'landing' | 'dashboard' | 'guest' | 'join' | 'session' | 'auth' | 'resetPassword' | 'terms' | 'privacy' | 'about' | 'help';

export default function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  
  const [view, setView] = useState<View>('landing');
  const [sessionLesson, setSessionLesson] = useState<Lesson | null>(null);
  
  const [isHighContrast, setIsHighContrast] = useState(false);
  const [isDyslexicFont, setIsDyslexicFont] = useState(false);
  const [isVoiceOver, setIsVoiceOver] = useState(false);
  const [isAccessibilityModalOpen, setAccessibilityModalOpen] = useState(false);
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [authInitialView, setAuthInitialView] = useState<'login' | 'register' | 'forgotPassword'>('login');
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  const [newBadgeQueue, setNewBadgeQueue] = useState<Badge[]>([]);

  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [mfaSetupInfo, setMfaSetupInfo] = useState({ active: false });
  const [mfaVerifyInfo, setMfaVerifyInfo] = useState({ active: false, mfaToken: '' });
  const [forceResetInfo, setForceResetInfo] = useState({ active: false, resetToken: '' });

  const { showBanner, consent, acceptAll, rejectAll, savePreferences } = useCookieConsent();
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);

  const handleLogout = useCallback((redirect = true) => {
    localStorage.removeItem("token");
    setToken(null);
    setCurrentUser(null);
    if(redirect) setView('landing');
    setShowInactivityModal(false);
  }, []);

  const { isPrompted } = useInactivityTimer({
    onIdle: handleLogout,
    onPrompt: () => setShowInactivityModal(true),
    isLoggedIn: !!currentUser
  });

  useEffect(() => {
     if (!isPrompted) {
        setShowInactivityModal(false);
     }
  }, [isPrompted]);


  useEffect(() => {
    const initializeApp = async () => {
        setIsLoading(true);
        setError(null);
        
        const params = new URLSearchParams(window.location.search);
        const resetToken = params.get('resetToken');
        const inviteToken = params.get('invitation_token');
        
        if (resetToken) {
            setView('resetPassword');
            setIsLoading(false);
            return;
        }

        if (inviteToken) {
            setInvitationToken(inviteToken);
            openAuthModal('register');
            window.history.replaceState({}, document.title, window.location.pathname); // Clean URL
            setIsLoading(false);
            return;
        }

        if (token) {
            try {
                const userData = await api.get<User>('/api/v1/auth/me');
                setCurrentUser(userData);
                setView('dashboard');
            } catch (e) {
                console.error("Auth check failed:", e);
                const errorMessage = e instanceof Error ? e.message : "An unknown authentication error occurred.";
                setError(`Your session may have expired or the server is unavailable. Please try logging in again. (Reason: ${errorMessage})`);
                handleLogout(false);
                setView('landing');
            }
        } else {
            const code = params.get('code');
            if (code) setView('join');
            else setView('landing');
        }
        setIsLoading(false);
    };
    initializeApp();
  }, [token, handleLogout]);

  const handleLogin = async (newToken: string, userData: User) => {
    if (userData.password_reset_required) {
        setForceResetInfo({ active: true, resetToken: newToken });
        return;
    }
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setCurrentUser(userData);
    setView('dashboard');
    setAuthModalOpen(false);
    setMfaVerifyInfo({ active: false, mfaToken: '' });
    if (userData.mfa_setup_required) {
        setMfaSetupInfo({ active: true });
    }
  };

  const handleForceResetSuccess = () => {
      setForceResetInfo({ active: false, resetToken: '' });
      handleLogout(false); // Log them out without redirecting
      openAuthModal('login', 'Your password has been updated. Please log in again.');
  }

  const promptMfaVerify = (mfaToken: string) => {
      setMfaVerifyInfo({ active: true, mfaToken });
      setAuthModalOpen(false);
  };

  const handleNewBadges = (newBadges: Badge[]) => {
    setNewBadgeQueue(prev => [...prev, ...newBadges]);
  };

  const handleCloseBadgeModal = () => {
    setNewBadgeQueue(prev => prev.slice(1));
  };
  
  const handleStartSession = (lesson: Lesson) => {
    setSessionLesson(lesson);
    setView('session');
  }
  
  const openAuthModal = (initialView: 'login' | 'register' | 'forgotPassword', message?: string) => {
    setAuthInitialView(initialView);
    setAuthModalOpen(true);
    // You could pass the message to the AuthPage if you want to display it
  }

  const handleSaveCookiePreferences = (preferences: CookieConsent) => {
      savePreferences(preferences);
      setIsCookieModalOpen(false);
  };

  const renderContent = () => {
    if (isLoading) return <LoadingSpinner message="Loading application..." />;
    if (error && view === 'landing') {
        return (
            <div className="w-full max-w-2xl mx-auto p-4 text-center">
                <h2 className="text-2xl font-bold text-red-700 mb-4">An Error Occurred</h2>
                <div className="text-red-600 bg-red-100 p-4 rounded-md mb-6">{error}</div>
                <LandingPage openAuthModal={openAuthModal} setView={setView} />
            </div>
        );
    }


    switch(view) {
        case 'guest': return <GuestDashboard />;
        case 'join': return <JoinWithCode onStartSession={handleStartSession} />;
        case 'session': 
            return sessionLesson ? 
                <TypingTutor 
                    lesson={sessionLesson} 
                    onLessonComplete={() => { /* Guest session progress isn't saved */ }} 
                    onExit={() => { setSessionLesson(null); setView('join'); }}
                /> 
                : <JoinWithCode onStartSession={handleStartSession} />;
        case 'resetPassword': return <PasswordResetPage openAuthModal={openAuthModal} />;
        case 'terms': return <TermsOfServicePage />;
        case 'privacy': return <PrivacyPolicyPage />;
        case 'about': return <AboutUsPage />;
        case 'help': return <HelpCenterPage />;
        case 'dashboard':
            if (!currentUser) return <LandingPage openAuthModal={openAuthModal} setView={setView} />;
            switch (currentUser.role) {
                case 'student': return <StudentDashboard user={currentUser} onNewBadges={handleNewBadges} />;
                case 'teacher': return <TeacherDashboard user={currentUser} />;
                case 'admin': return <AdminDashboard user={currentUser} />;
                case 'super_admin': return <SuperAdminDashboard user={currentUser} />;
                case 'parent': return <ParentDashboard user={currentUser} />;
                default: return <p>Unknown user role: {currentUser.role}</p>;
            }
        case 'landing': default: return <LandingPage openAuthModal={openAuthModal} setView={setView} />;
    }
  };

  return (
    <AppContext.Provider value={{ handleLogin, promptMfaVerify }}>
      <AccessibilityContext.Provider value={{ isHighContrast, setIsHighContrast, isDyslexicFont, setIsDyslexicFont, isVoiceOver, setIsVoiceOver }}>
        <div className={`min-h-screen flex flex-col ${isHighContrast ? 'dark' : ''} ${isDyslexicFont ? 'font-dyslexic' : 'font-inter'} bg-gray-100`}>
          <Header user={currentUser} onLogout={handleLogout} onOpenAccessibilityModal={() => setAccessibilityModalOpen(true)} setView={setView} openAuthModal={openAuthModal} />
          <main className="p-4 md:p-8 flex justify-center items-start flex-grow">
            {renderContent()}
          </main>
          
          {isAuthModalOpen && <AuthPage initialView={authInitialView} onClose={() => setAuthModalOpen(false)} invitationToken={invitationToken} />}
          {isAccessibilityModalOpen && <AccessibilityModal onClose={() => setAccessibilityModalOpen(false)} />}
          
          {showInactivityModal && <InactivityWarningModal onLogout={() => handleLogout()} onStay={() => setShowInactivityModal(false)} />}
          {mfaSetupInfo.active && <MfaSetupModal onClose={() => setMfaSetupInfo({ active: false })} />}
          {mfaVerifyInfo.active && <MfaVerifyModal mfaToken={mfaVerifyInfo.mfaToken} onClose={() => setMfaVerifyInfo({ active: false, mfaToken: '' })} />}
          {forceResetInfo.active && <ForcePasswordResetPage resetToken={forceResetInfo.resetToken} onSuccess={handleForceResetSuccess} />}

          {newBadgeQueue.length > 0 && <BadgeAwardModal badge={newBadgeQueue[0]} onClose={handleCloseBadgeModal} />}
          <Footer setView={setView} onManageCookies={() => setIsCookieModalOpen(true)} />

          {showBanner && !isCookieModalOpen && (
              <CookieConsentBanner
                  onAcceptAll={acceptAll}
                  onRejectAll={rejectAll}
                  onManage={() => setIsCookieModalOpen(true)}
              />
          )}
          {isCookieModalOpen && (
              <CookieSettingsModal
                  consent={consent}
                  onClose={() => setIsCookieModalOpen(false)}
                  onSave={handleSaveCookiePreferences}
                  onAcceptAll={acceptAll}
                  onRejectAll={rejectAll}
              />
          )}
        </div>
      </AccessibilityContext.Provider>
    </AppContext.Provider>
  );
}